import React, { useMemo, useState } from 'react'
import { Plus, Edit2, Trash2, Users, X, Check, Layers } from 'lucide-react'
import { Team, RoleWithMember, OrgChartService } from '../../services/orgChartService'

interface TeamsViewProps {
  teams: Team[]
  roles: RoleWithMember[]
  isAdmin: boolean
  onCreateTeam: () => void
  onRefresh: () => void
}

export const TeamsView: React.FC<TeamsViewProps> = ({
  teams,
  roles,
  isAdmin,
  onCreateTeam,
  onRefresh
}) => {
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')
  const [saving, setSaving] = useState(false)

  const orgChartService = OrgChartService.getInstance()

  // Group roles by team (including inherited teams)
  // A role can appear in multiple teams now
  const rolesByTeam = useMemo(() => {
    const map = new Map<string, RoleWithMember[]>()

    // Initialize with all teams
    teams.forEach(team => {
      map.set(team.name, [])
    })

    // Group roles by their effective teams (direct teams or inherited)
    roles.forEach(role => {
      const directTeams = role.teams || []
      const inheritedTeams = role.inheritedTeams || []
      const effectiveTeams = directTeams.length > 0 ? directTeams : inheritedTeams
      
      // Add role to each team it belongs to
      effectiveTeams.forEach(teamName => {
        if (map.has(teamName)) {
          map.get(teamName)!.push(role)
        }
      })
    })

    return map
  }, [teams, roles])

  // Roles without any team
  const rolesWithoutTeam = useMemo(() => {
    return roles.filter(role => {
      const directTeams = role.teams || []
      const inheritedTeams = role.inheritedTeams || []
      return directTeams.length === 0 && inheritedTeams.length === 0
    })
  }, [roles])

  // Get unique members for a team (since same person can have multiple roles)
  const getUniqueMembersForTeam = (teamRoles: RoleWithMember[]) => {
    const seen = new Set<string>()
    const membersWithRoles: Array<{ member: RoleWithMember['assignedMember'], roles: RoleWithMember[] }> = []

    teamRoles.forEach(role => {
      if (role.assignedMember && !seen.has(role.assignedMember.id)) {
        seen.add(role.assignedMember.id)
        // Collect all roles for this member
        const memberRoles = teamRoles.filter(r => r.assignedMemberId === role.assignedMember!.id)
        membersWithRoles.push({
          member: role.assignedMember,
          roles: memberRoles
        })
      }
    })

    return membersWithRoles
  }

  const handleStartEdit = (team: Team) => {
    setEditingTeamId(team.id)
    setEditName(team.name)
    setEditColor(team.color)
  }

  const handleCancelEdit = () => {
    setEditingTeamId(null)
    setEditName('')
    setEditColor('')
  }

  const handleSaveEdit = async (teamId: string) => {
    if (!editName.trim()) return

    try {
      setSaving(true)
      await orgChartService.updateTeam(teamId, {
        name: editName.trim(),
        color: editColor
      })
      setEditingTeamId(null)
      onRefresh()
    } catch (error) {
      console.error('Error updating team:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTeam = async (teamId: string) => {
    if (!confirm('Are you sure you want to delete this team? Roles will be unassigned from this team.')) {
      return
    }

    try {
      setSaving(true)
      await orgChartService.deleteTeam(teamId)
      onRefresh()
    } catch (error) {
      console.error('Error deleting team:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Layers className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            {teams.length} Team{teams.length !== 1 ? 's' : ''}
          </h2>
        </div>

        {isAdmin && (
          <button
            onClick={onCreateTeam}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Team</span>
          </button>
        )}
      </div>

      {/* Teams grid */}
      {teams.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Teams Yet</h3>
          <p className="text-gray-500 mb-4">Create teams to organize your org chart by department or function</p>
          {isAdmin && (
            <button
              onClick={onCreateTeam}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Create First Team</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {teams.map(team => {
            const teamRoles = rolesByTeam.get(team.name) || []
            const membersWithRoles = getUniqueMembersForTeam(teamRoles)
            const vacantRoles = teamRoles.filter(r => !r.assignedMember)
            const isEditing = editingTeamId === team.id

            return (
              <div
                key={team.id}
                className="bg-white rounded-lg border-2 overflow-hidden"
                style={{ borderColor: team.color }}
              >
                {/* Team header */}
                <div 
                  className="px-4 py-3 flex items-center justify-between"
                  style={{ backgroundColor: `${team.color}15` }}
                >
                  {isEditing ? (
                    <div className="flex items-center space-x-3 flex-1">
                      <input
                        type="color"
                        value={editColor}
                        onChange={(e) => setEditColor(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                      />
                      <button
                        onClick={handleCancelEdit}
                        className="p-1.5 text-gray-500 hover:text-gray-700 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleSaveEdit(team.id)}
                        disabled={saving}
                        className="p-1.5 text-green-600 hover:text-green-700 rounded disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: team.color }}
                        />
                        <h3 
                          className="font-semibold text-lg"
                          style={{ color: team.color }}
                        >
                          {team.name}
                        </h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <Users className="w-4 h-4" />
                          <span>{membersWithRoles.length} member{membersWithRoles.length !== 1 ? 's' : ''}</span>
                          {vacantRoles.length > 0 && (
                            <span className="text-amber-600">
                              • {vacantRoles.length} vacant
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {isAdmin && (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleStartEdit(team)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTeam(team.id)}
                            disabled={saving}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Team members */}
                <div className="p-4">
                  {membersWithRoles.length === 0 && vacantRoles.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">
                      No roles assigned to this team yet
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {/* Filled roles */}
                      {membersWithRoles.map(({ member, roles: memberRoles }) => (
                        <div 
                          key={member!.id} 
                          className="bg-gray-50 rounded-lg p-3 border border-gray-100"
                        >
                          <div className="flex items-center space-x-3 mb-2">
                            <img
                              src={member!.thumbnailPhotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(member!.name.fullName)}&background=8b5cf6&color=fff`}
                              alt={member!.name.fullName}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 truncate text-sm">
                                {member!.name.fullName}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {member!.primaryEmail}
                              </p>
                            </div>
                          </div>
                          
                          {/* Roles in this team */}
                          <div className="flex flex-wrap gap-1">
                            {memberRoles.map(role => (
                              <span 
                                key={role.id}
                                className="px-2 py-0.5 text-xs rounded-full"
                                style={{ 
                                  backgroundColor: `${team.color}20`,
                                  color: team.color
                                }}
                              >
                                {role.title}
                              </span>
                            ))}
                          </div>

                          {/* Multi-team indicator */}
                          {(() => {
                            // Check if this person has roles in multiple teams
                            const personRoles = roles.filter(pr => pr.assignedMemberId === member!.id)
                            const allTeamNames = new Set<string>()
                            personRoles.forEach(pr => {
                              const directTeams = pr.teams || []
                              const inheritedTeams = pr.inheritedTeams || []
                              const effectiveTeams = directTeams.length > 0 ? directTeams : inheritedTeams
                              effectiveTeams.forEach(t => allTeamNames.add(t))
                            })
                            return allTeamNames.size > 1
                          })() && (
                            <div className="mt-2 text-xs text-gray-400 flex items-center space-x-1">
                              <Layers className="w-3 h-3" />
                              <span>Multi-team</span>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Vacant roles */}
                      {vacantRoles.map(role => (
                        <div 
                          key={role.id}
                          className="bg-gray-50 rounded-lg p-3 border border-dashed border-gray-300"
                        >
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <Users className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-400 truncate text-sm">
                                Vacant
                              </p>
                              <p className="text-xs text-gray-400 italic">
                                Role to fill
                              </p>
                            </div>
                          </div>
                          <span 
                            className="px-2 py-0.5 text-xs rounded-full inline-block"
                            style={{ 
                              backgroundColor: `${team.color}20`,
                              color: team.color
                            }}
                          >
                            {role.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {/* Unassigned roles (no team) */}
          {rolesWithoutTeam.length > 0 && (
            <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full bg-gray-300" />
                  <h3 className="font-semibold text-lg text-gray-500">No Team Assigned</h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-400">
                    <span>{rolesWithoutTeam.length} role{rolesWithoutTeam.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {rolesWithoutTeam.map(role => (
                    <div 
                      key={role.id}
                      className={`bg-gray-50 rounded-lg p-3 border ${
                        role.assignedMember ? 'border-gray-100' : 'border-dashed border-gray-300'
                      }`}
                    >
                      {role.assignedMember ? (
                        <>
                          <div className="flex items-center space-x-3 mb-2">
                            <img
                              src={role.assignedMember.thumbnailPhotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(role.assignedMember.name.fullName)}&background=8b5cf6&color=fff`}
                              alt={role.assignedMember.name.fullName}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 truncate text-sm">
                                {role.assignedMember.name.fullName}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {role.assignedMember.primaryEmail}
                              </p>
                            </div>
                          </div>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">
                            {role.title}
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <Users className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-400 truncate text-sm">
                                Vacant
                              </p>
                            </div>
                          </div>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-500">
                            {role.title}
                          </span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
